import type { Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { DisciplineCode } from '@brighterly/lib-core-types/student-assessment/types'
import type { Grade } from './enums'

import { useAssessmentTemplatesStore } from '../assessment-templates/store'
import { useAssessmentTemplateStoreApi } from '../../api/assessment-templates/store.hook'
import { AssessmentType } from './enums'
import {
  assessmentTypeOptions,
  disciplineCodeOptions,
  gradeOptions,
  getAssessmentEditPath,
  isTemplateTitleValid,
} from './assessment-template-options'
import { buildEmptyAssessmentDocument } from './empty-document'

interface ModalCreateAssessmentTemplateReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refTitle: Ref<string>
  refType: Ref<AssessmentType | undefined>
  refDisciplineCode: Ref<DisciplineCode | undefined>
  refGrade: Ref<Grade | undefined>
  refIsValid: ComputedRef<boolean>
  typeOptions: AppSelectOption<AssessmentType>[]
  disciplineOptions: AppSelectOption<DisciplineCode>[]
  gradeSelectOptions: AppSelectOption<Grade>[]
  setTitle: (value: string | number | null) => void
  closeModal: () => void
  createTemplate: () => Promise<void>
}

export const useModalCreateAssessmentTemplate = (): ModalCreateAssessmentTemplateReturn => {
  const router = useRouter()
  const store = useAssessmentTemplatesStore()

  const refIsLoading = ref(false)
  const refTitle = ref('')
  // `BASELINE` is the only member today (§0), so it is the sensible default rather than an empty
  // select the admin must always touch.
  const refType = ref<AssessmentType | undefined>(AssessmentType.Baseline)
  const refDisciplineCode = ref<DisciplineCode | undefined>()
  const refGrade = ref<Grade | undefined>()

  const refIsOpen = computed<boolean>(() => store.isModalCreate)

  const refIsValid = computed<boolean>(() =>
    isTemplateTitleValid(refTitle.value)
    && !!refType.value
    && !!refDisciplineCode.value
    && !!refGrade.value)

  const setTitle = (value: string | number | null): void => {
    refTitle.value = String(value ?? '')
  }

  const closeModal = (): void => {
    store.setModalCreate(false)
    refTitle.value = ''
    refType.value = AssessmentType.Baseline
    refDisciplineCode.value = undefined
    refGrade.value = undefined
  }

  /**
   * Posts the four row fields plus an EMPTY starting document — `data` is required by the API and
   * the editor has not run yet. Not validated on the way out (B9): a screenless assessment is
   * legitimately incomplete while it is being authored.
   */
  const createTemplate = async (): Promise<void> => {
    const type = refType.value
    const disciplineCode = refDisciplineCode.value
    const grade = refGrade.value

    if (!refIsValid.value || refIsLoading.value || !type || !disciplineCode || !grade) return

    const title = refTitle.value.trim()

    await useAssessmentTemplateStoreApi({
      payload: {
        title,
        type,
        discipline_code: disciplineCode,
        grade,
        data: buildEmptyAssessmentDocument({ title, disciplineCode, grade }),
      },
      onStart: () => { refIsLoading.value = true },
      onSuccess: (template) => {
        closeModal()
        void router.push(getAssessmentEditPath(template.id))
      },
      onFinish: () => { refIsLoading.value = false },
    })
  }

  return {
    refIsOpen,
    refIsLoading,
    refTitle,
    refType,
    refDisciplineCode,
    refGrade,
    refIsValid,
    typeOptions: assessmentTypeOptions,
    disciplineOptions: disciplineCodeOptions,
    gradeSelectOptions: gradeOptions,
    setTitle,
    closeModal,
    createTemplate,
  }
}
